import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Plus, Edit2, Trash2, Star, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FEATURED_OPTIONS = [
  { value: "home", label: "Home Page" },
  { value: "retail", label: "Retail Investors Page" },
  { value: "corporate", label: "Corporate Clients Page" },
  { value: "none", label: "None (Hidden)" }
];

export default function AdminTestimonials() {
  const [testimonials, setTestimonials] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    client_name: "",
    designation: "",
    company: "",
    content: "",
    rating: 5,
    featured_on: "home"
  });
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (!token) navigate("/admin/login");
    fetchTestimonials();
  }, [navigate]);

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/testimonials`);
      setTestimonials(response.data);
    } catch (error) {
      console.error("Error fetching testimonials:", error);
    }
  };

  const resetForm = () => {
    setFormData({
      client_name: "",
      designation: "",
      company: "",
      content: "",
      rating: 5,
      featured_on: "home"
    });
    setEditingId(null);
  };

  const handleOpenModal = (testimonial = null) => {
    if (testimonial) {
      setFormData({
        client_name: testimonial.client_name,
        designation: testimonial.designation || "",
        company: testimonial.company || "",
        content: testimonial.content,
        rating: testimonial.rating || 5,
        featured_on: testimonial.featured_on || (testimonial.is_featured ? "home" : "none")
      });
      setEditingId(testimonial.id);
    } else {
      resetForm();
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${BACKEND_URL}/api/admin/testimonials/${editingId}`, formData);
        toast.success("Testimonial updated successfully");
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/testimonials`, formData);
        toast.success("Testimonial created successfully");
      }
      handleCloseModal();
      fetchTestimonials();
    } catch (error) {
      toast.error(editingId ? "Failed to update testimonial" : "Failed to create testimonial");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this testimonial?")) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/testimonials/${id}`);
      toast.success("Testimonial deleted successfully");
      fetchTestimonials();
    } catch (error) {
      toast.error("Failed to delete testimonial");
    }
  };

  const getFeaturedLabel = (featured_on) => {
    const option = FEATURED_OPTIONS.find(opt => opt.value === featured_on);
    return option ? option.label : "None";
  };

  const getFeaturedColor = (featured_on) => {
    switch (featured_on) {
      case 'home': return 'bg-primary/20 text-primary';
      case 'retail': return 'bg-green-500/20 text-green-400';
      case 'corporate': return 'bg-blue-500/20 text-blue-400';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  return (
    <div className="min-h-screen bg-background py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8 flex items-center justify-between">
          <div className="flex items-center">
            <Link to="/admin">
              <Button variant="ghost"><ArrowLeft className="mr-2 h-4 w-4" /> Back</Button>
            </Link>
            <h1 className="font-heading text-4xl font-bold text-text-primary ml-4">Manage Testimonials</h1>
          </div>
          <Button onClick={() => handleOpenModal()} className="btn-primary" data-testid="add-testimonial-btn">
            <Plus className="mr-2 h-4 w-4" /> Add Testimonial
          </Button>
        </div>

        <div className="glass-effect rounded-2xl overflow-hidden">
          <div className="overflow-x-auto admin-table">
            <table>
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Designation</th>
                  <th>Rating</th>
                  <th>Featured On</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {testimonials.map((testimonial) => (
                  <tr key={testimonial.id}>
                    <td>
                      <div>
                        <span className="font-semibold">{testimonial.client_name}</span>
                        {testimonial.company && (
                          <span className="text-text-muted text-sm block">{testimonial.company}</span>
                        )}
                      </div>
                    </td>
                    <td>{testimonial.designation || "-"}</td>
                    <td>
                      <div className="flex items-center">
                        {[...Array(testimonial.rating || 5)].map((_, i) => (
                          <Star key={i} className="h-4 w-4 fill-primary text-primary" />
                        ))}
                      </div>
                    </td>
                    <td>
                      <span className={`px-2 py-1 rounded-full text-xs ${getFeaturedColor(testimonial.featured_on || (testimonial.is_featured ? 'home' : 'none'))}`}>
                        {getFeaturedLabel(testimonial.featured_on || (testimonial.is_featured ? 'home' : 'none'))}
                      </span>
                    </td>
                    <td>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => handleOpenModal(testimonial)} data-testid={`edit-testimonial-${testimonial.id}`}>
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button variant="destructive" size="sm" onClick={() => handleDelete(testimonial.id)} data-testid={`delete-testimonial-${testimonial.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {testimonials.length === 0 && (
                  <tr>
                    <td colSpan="5" className="text-center py-8 text-text-muted">
                      No testimonials yet. Add your first testimonial!
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="glass-effect rounded-2xl p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-heading text-2xl font-bold text-text-primary">
                {editingId ? 'Edit Testimonial' : 'Add Testimonial'}
              </h2>
              <Button variant="ghost" size="sm" onClick={handleCloseModal}>
                <X className="h-5 w-5" />
              </Button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-text-secondary text-sm mb-2">Client Name *</label>
                <input
                  type="text"
                  value={formData.client_name}
                  onChange={(e) => setFormData({...formData, client_name: e.target.value})}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:border-primary"
                  required
                  data-testid="testimonial-client-name"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-text-secondary text-sm mb-2">Designation</label>
                  <input
                    type="text"
                    value={formData.designation}
                    onChange={(e) => setFormData({...formData, designation: e.target.value})}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:border-primary"
                    placeholder="e.g., Software Engineer"
                    data-testid="testimonial-designation"
                  />
                </div>
                <div>
                  <label className="block text-text-secondary text-sm mb-2">Company</label>
                  <input
                    type="text"
                    value={formData.company}
                    onChange={(e) => setFormData({...formData, company: e.target.value})}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:border-primary"
                    placeholder="e.g., Tech Corp"
                    data-testid="testimonial-company"
                  />
                </div>
              </div>

              <div>
                <label className="block text-text-secondary text-sm mb-2">Testimonial Content *</label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({...formData, content: e.target.value})}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:border-primary min-h-[120px]"
                  required
                  placeholder="What did the client say about your services?"
                  data-testid="testimonial-content"
                />
              </div>

              <div>
                <label className="block text-text-secondary text-sm mb-2">Rating</label>
                <select
                  value={formData.rating}
                  onChange={(e) => setFormData({...formData, rating: parseInt(e.target.value)})}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:border-primary"
                  data-testid="testimonial-rating"
                >
                  {[5, 4, 3, 2, 1].map(num => (
                    <option key={num} value={num}>{num} Star{num > 1 ? 's' : ''}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-text-secondary text-sm mb-3">Featured On *</label>
                <div className="space-y-2">
                  {FEATURED_OPTIONS.map((option) => (
                    <label key={option.value} className="flex items-center cursor-pointer group">
                      <input
                        type="radio"
                        name="featured_on"
                        value={option.value}
                        checked={formData.featured_on === option.value}
                        onChange={(e) => setFormData({...formData, featured_on: e.target.value})}
                        className="w-4 h-4 text-primary bg-white/5 border-white/20 focus:ring-primary"
                        data-testid={`testimonial-featured-${option.value}`}
                      />
                      <span className="ml-3 text-text-secondary group-hover:text-text-primary transition-colors">
                        {option.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={handleCloseModal} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" className="btn-primary flex-1" data-testid="testimonial-submit">
                  {editingId ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
